export const MOCKUP_OPTIONS = [
  { group: 'Meta',      label: 'Meta – Single Image / Video',  value: 'meta' },
  { group: 'Meta',      label: 'Meta – Carousel',              value: 'meta-carousel' },
  { group: 'Meta',      label: 'Meta – Collection',            value: 'meta-collection' },
  { group: 'Meta',      label: 'Meta – Dynamic Creative',      value: 'meta-dynamic-creative' },
  { group: 'Meta',      label: 'Meta – Flexible Ad',           value: 'meta-flexible-ad' },
  { group: 'TikTok',    label: 'TikTok',                       value: 'tiktok' },
  { group: 'Snapchat',  label: 'Snapchat',                     value: 'snapchat' },
  { group: 'Snapchat',  label: 'Snapchat – Story Ad',          value: 'snapchat-story' },
  { group: 'LinkedIn',  label: 'LinkedIn',                     value: 'linkedin' },
  { group: 'LinkedIn',  label: 'LinkedIn – Carousel',          value: 'linkedin-carousel' },
  { group: 'LinkedIn',  label: 'LinkedIn – Document',          value: 'linkedin-document' },
] as const;

// MockupType includes both the user-facing dropdown values and internal placement variants
// used by resolveEffectiveMockupType (story/reels variants are not in the dropdown —
// they're accessed via ratio tabs on their parent formats).
export type MockupType =
  | typeof MOCKUP_OPTIONS[number]['value']
  | 'meta-reels'
  | 'meta-story'
  | 'meta-story-carousel'
  | 'meta-story-collection';

export const MOCKUP_GROUPS = [...new Set(MOCKUP_OPTIONS.map(o => o.group))];

// Formats with a completed ad-details component — shown with a ✓ in the dev dropdown
export const COMPLETED_MOCKUP_TYPES = new Set<MockupType>(['meta', 'meta-carousel', 'meta-collection', 'meta-dynamic-creative', 'meta-flexible-ad', 'tiktok', 'snapchat', 'snapchat-story', 'linkedin', 'linkedin-carousel', 'linkedin-document']);

// Story-format mockups that need an explicit 360×640 container (they use w-full/h-full internally)
export const STORY_FORMAT_TYPES: MockupType[] = [
  'meta-story',
  'meta-story-carousel',
  'meta-story-collection',
  'snapchat-story',
];

// Maps campaign asset_type_id → MockupType for the preview page.
// Add new entries here when new asset types get mockups.
export const ASSET_TYPE_TO_MOCKUP_TYPE: Record<string, MockupType> = {
  'meta-unified-social-ad':        'meta',
  'meta-unified-carousel':         'meta-carousel',
  'meta-unified-dynamic-creative': 'meta-dynamic-creative',
  'meta-unified-collection':       'meta-collection',
  'meta-flexible-ad':              'meta-flexible-ad',
  'linkedin-1x1-social-ad':        'linkedin',
  'linkedin-1x1-carousel':         'linkedin-carousel',
  'linkedin-document':             'linkedin-document',
  'tiktok-ad':                     'tiktok',
  'snapchat-9x16-single-image':    'snapchat',
  'snapchat-9x16-story':           'snapchat-story',
};

export interface Variant {
  label: string;
  hasData: boolean;
}

export interface Comment {
  id?: string;
  author: string;
  author_email?: string;
  initials: string;
  date: string;
  type: 'general' | 'rejection' | 'approval';
  text: string;
  can_delete?: boolean;
  approvalNote?: string;
  mentions?: { user_id: string; name: string | null; email?: string }[];
}

export interface Campaign {
  platform: string;
  quarter: string;
  market: string;
  businessUnit: string;
  nameplate: string;
  buyType: string;
  tier: string;
  format: string;
  fordshareId: string;
  linkToAsset?: string;
}

/** Metadata for a single warning — used to identify AI warnings and submit feedback. */
export interface WarningMeta {
  rule: string;
  field: string;
  key: string;
}

/** All validation issues for a single form field, split by severity. */
export interface FieldIssue {
  errors: string[];
  warnings: string[];
  /** Parallel to `warnings` — carries rule/field/key for each warning so InlineIssue can render AI feedback actions. */
  warningMeta?: WarningMeta[];
}

/** Dominant severity of a FieldIssue — 'error' beats 'warning', undefined if empty. */
export function fieldIssueDominant(fi: FieldIssue | undefined): 'error' | 'warning' | undefined {
  if (!fi) return undefined;
  if (fi.errors.length > 0) return 'error';
  if (fi.warnings.length > 0) return 'warning';
  return undefined;
}
